import axios, { AxiosResponse } from "axios";
import UserService from "./UserService";
import SweepBase, {
  EmptyPosition,
  ReadingUpdateModel,
  SweepCreate,
  SweepInfo,
  SweepPaginated,
  SweepPositionShow,
  SweepShow,
  UntrackedPositionShowSchema,
} from "../models/Sweep";
import { NavigateFunction } from "react-router-dom";
import { notification } from "antd";

const API_BASE_URL = window._env_.REACT_APP_ARCOR_CENTRAL_API_URL;
const API_RESOURCE = "sweeps/";

export default class SweepService {
  public static async getInfo(id: string,navigate: NavigateFunction): Promise<SweepInfo | null> {
    try {
      const response: AxiosResponse<SweepInfo> = await axios.get(
        API_BASE_URL + API_RESOURCE + id + "/info",
        {
          headers: UserService.getAuthHeader(),
        }
      );
      const sweep = SweepInfo.from(response.data);
      return sweep;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response) {
        notification.error({
          message: "Insufficient Credentials",
          description:"Update your credentials to access here"
        });
        if (error.response.status === 401) {
          sessionStorage.removeItem("token")
          navigate("/login")
          return null;
        }
      }
      return null;
    }
  }
  public static async getOne(id: string,navigate: NavigateFunction): Promise<SweepShow | null> {
    try {
      const response: AxiosResponse<SweepShow> = await axios.get(
        API_BASE_URL + API_RESOURCE + id + "/",
        {
          headers: UserService.getAuthHeader(),
        }
      );
      const sweep = SweepShow.from(response.data);
      return sweep;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response) {
        notification.error({
          message: "Insufficient Credentials",
          description:"Update your credentials to access here"
        });
        if (error.response.status === 401) {
          sessionStorage.removeItem("token")
          navigate("/login")
          return null;
        }
      }
      return null;
    }
  }
  public static async getAll(navigate: NavigateFunction, skip: number = 1, limit: number = 10, warehouseFilter: string[] = [], statusFilter: string[] = [], countingNumberFilter: string = "", preload: boolean = false, signal?: AbortSignal): Promise<SweepPaginated | null> {
    try {
      const params = new URLSearchParams();
      params.append("page", skip.toString());
      params.append("page_size", limit.toString());
      warehouseFilter.forEach(warehouse => params.append("warehouses_idx", warehouse));
      statusFilter.forEach(status => params.append("status", status));
      if (countingNumberFilter) {
        params.append("counting_number", countingNumberFilter);
      }
      params.append("preload", preload.toString());

      const response: AxiosResponse<SweepPaginated> = await axios.get(
        `${API_BASE_URL}${API_RESOURCE}?${params.toString()}`,
        {
          headers: UserService.getAuthHeader(),
          signal: signal,
        }
      );
      const sweeps = SweepPaginated.from(response.data);
      return sweeps;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response) {
        notification.error({
          message: "Insufficient Credentials",
          description: "Update your credentials to access here"
        });
        if (error.response.status === 401) {
          sessionStorage.removeItem("token");
          navigate("/login");
          return null;
        }
      }
      return null;
    }
  }
  public static async create(
    sweep_create: SweepCreate
  ): Promise<string | null> {
    try {
      const response: AxiosResponse<string> = await axios.post(
        API_BASE_URL + API_RESOURCE + "create",
        sweep_create,
        {
          headers: UserService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }
  public static async updateStatus(
    sweep_base: SweepBase
  ): Promise<SweepBase | null> {
    try {
      const response: AxiosResponse<SweepBase> = await axios.put(
        API_BASE_URL + API_RESOURCE + "status",
        sweep_base,
        {
          headers: UserService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public static async updateReading(
    reading: ReadingUpdateModel
  ): Promise<string | null> {
    try {
      const response: AxiosResponse<string> = await axios.put(
        API_BASE_URL + API_RESOURCE + "update",
        reading,
        {
          headers: UserService.getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public static async exportCSV(idx: string): Promise<void> {
    try {
      const response: AxiosResponse = await axios.post(
        API_BASE_URL + API_RESOURCE + idx + "/csv",
        { display_unknown: false },
        {
          headers: UserService.getAuthHeader(),
          responseType: "blob",
        }
      );
      // Create a blob from the response data
      const blob = new Blob([response.data], { type: "text/csv" });

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a link element to trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "positions.csv");
      document.body.appendChild(link);

      // Trigger the download
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading positions CSV:", error);
      // Handle error
    }
  }
  public static async statusCSV(
    idx: string
  ): Promise<Boolean > {
    try {
      const response: AxiosResponse<Boolean> = await axios.get(
        API_BASE_URL + API_RESOURCE + idx + "/csv-status",
        {
          headers: UserService.getAuthHeader(),
        }
      );

      return response.data;
    } catch (error) {
      console.error(error);
      return false;
    }
  }
  
  public static async getSweepPositionsByCorridor(
    id: string,
    corridor: string
  ): Promise<SweepPositionShow[] | null> {
    try {
      const response: AxiosResponse<SweepPositionShow[]> = await axios.get(
        API_BASE_URL + API_RESOURCE + id + "/" + corridor + "/sweep_positions",
        {
          headers: UserService.getAuthHeader(),
        }
      );
      const positions = response.data;
      return positions;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public static async getUntrakedPositions(sweepId: string): Promise<UntrackedPositionShowSchema[] | null> {
    try {
      const response: AxiosResponse<UntrackedPositionShowSchema[]> = await axios.get(
        API_BASE_URL + API_RESOURCE + sweepId  + "/missing_positions",
        {
          headers: UserService.getAuthHeader(),
        }
      );
      const positions = response.data;
      return positions;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public static async setAllUntrakedPositon(sweepId: string, action: string): Promise<string | null> {
    try {
      const response: AxiosResponse<string> = await axios.get(
        API_BASE_URL + API_RESOURCE + sweepId  + "/missing_positions/" + action + "/",
        {
          headers: UserService.getAuthHeader(),
        }
      );
      const positions = response.data;
      return positions;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public static async setUntrakedPositon(sweepId: string,action: string, positionId: string): Promise<string | null> {
    try {
      const response: AxiosResponse<string> = await axios.get(
        API_BASE_URL + API_RESOURCE + sweepId  + "/missing_positions/" + action + "/" + positionId,
        {
          headers: UserService.getAuthHeader(),
        }
      );
      const positions = response.data;
      return positions;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public static async setEmptyPosition(sweepId: string, corridor_name: string, empty_positions: EmptyPosition): Promise<string | null> {
    try {
      const response: AxiosResponse<string> = await axios.put(
        API_BASE_URL + API_RESOURCE + sweepId  + "/empty_positions/" + corridor_name,
        empty_positions,
        {
          headers: UserService.getAuthHeader(),
        }
      );
      const positions = response.data;
      return positions;
    } catch (error) {
      console.error(error);
      return null;
    }
  }
}
